import React, { useEffect, useId, useRef, useState } from "react";
import styles from "./Modal.module.css";

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  showCloseButton = true,
  fullWidth = false,
  bottomPosition = false,
  animate = true,
  "data-testid": testId = "offers-dialog",
}) => {
  const modalRef = useRef(null);
  const titleId = useId();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure the DOM is rendered before animation starts
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      if (animate) {
        // Wait for animation to complete before removing from DOM
        const timer = setTimeout(() => setShouldRender(false), 300);
        return () => clearTimeout(timer);
      } else {
        setShouldRender(false);
      }
    }
  }, [isOpen, animate]);

  useEffect(() => {
    // Handle clicking outside the modal to close it
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Handle ESC key to close modal
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscKey);
      // Prevent scrolling when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  if (!shouldRender) return null;

  const overlayClasses = [
    styles.modalOverlay,
    bottomPosition && styles.modalOverlayBottom,
    animate && styles.modalOverlayAnimated,
    animate && isVisible && styles.modalOverlayVisible,
  ]
    .filter(Boolean)
    .join(" ");

  const containerClasses = [
    styles.modalContainer,
    fullWidth && styles.modalContainerFullWidth,
    animate && bottomPosition && styles.modalContainerSlide,
    animate && !bottomPosition && styles.modalContainerFade,
    animate && isVisible && styles.modalContainerVisible,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={overlayClasses} data-testid={`${testId}-overlay`}>
      <div className={containerClasses} ref={modalRef} data-testid={testId} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className={styles.modalHeader}>
          <h2 id={titleId} className={styles.modalTitle}>{title || "Modal"}</h2>
          <button data-testid="dialog-close" aria-label="Close dialog" className={styles.modalCloseButton} onClick={onClose}>
            ×
          </button>
        </div>

        <hr className={styles.modalDivider} />

        <div className={styles.modalContent}>{children}</div>

        {showCloseButton && (
          <div className={styles.modalFooter}>
            <button className={styles.modalCloseBtn} onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
